import { supabase } from '../lib/supabase';
import { errorHandler } from '../utils/errorHandler';
import { validationUtils } from '../utils/validation';
import { performanceUtils, dataCache } from '../utils/performance';

export const courseService = {
  async getAllCourses(filters = {}) {
    return errorHandler?.withRetry(async () => {
      // Check cache
      const cacheKey = `courses_${JSON.stringify(filters)}`;
      const cached = dataCache?.get(cacheKey);
      if (cached) return cached;
      
      let query = supabase?.from('courses')?.select(`
        *,
        institute:institute_id(
          id,
          institute_name,
          logo_url,
          user:user_id(city, state)
        )
      `);
    
      if (filters?.searchQuery) {
        const sanitizedQuery = validationUtils?.sanitizeString(filters?.searchQuery);
        query = query?.or(`course_name.ilike.%${sanitizedQuery}%,specialization.ilike.%${sanitizedQuery}%`);
      }
      
      if (filters?.category && filters?.category !== 'all') {
        query = query?.eq('category', filters?.category);
      }
      
      if (filters?.level && filters?.level !== 'all') {
        query = query?.eq('level', filters?.level);
      }
      
      if (filters?.courseType && filters?.courseType !== 'all') {
        query = query?.eq('course_type', filters?.courseType);
      }

      // Apply limit to prevent loading too many records at once
      const limit = filters?.limit || 100;
      query = query?.limit(limit);
      
      const { data, error } = await query?.order('course_name', { ascending: true });
      
      if (error) throw error;
      
      const transformed = data?.map(course => ({
        id: course?.id,
        courseName: course?.course_name,
        category: course?.category,
        courseType: course?.courseType,
        specialization: course?.specialization,
        level: course?.level,
        duration: course?.duration,
        fees: course?.fees,
        showFee: course?.show_fee !== false,
        eligibility: course?.eligibility,
        admissionProcess: course?.admission_process,
        placementStats: course?.placement_stats,
        accreditation: course?.accreditation,
        instituteId: course?.institute_id,
        institute: course?.institute ? {
          id: course?.institute?.id,
          name: course?.institute?.institute_name,
          logoUrl: course?.institute?.logo_url,
          city: course?.institute?.user?.city,
          state: course?.institute?.user?.state
        } : null,
        createdAt: course?.created_at
      })) || [];
      
      // Cache result
      dataCache?.set(cacheKey, transformed);
      
      return transformed;
    }, { maxRetries: 2, retryDelay: 1000 });
  },

  async getCourseById(courseId) {
    return errorHandler?.handleAsync(async () => {
      if (!validationUtils?.isValidUUID(courseId)) {
        throw new Error('Invalid course ID');
      }
      
      const { data, error } = await supabase?.from('courses')?.select(`
        *,
        institute:institute_id(
          id,
          institute_name,
          logo_url,
          description,
          website,
          user:user_id(city, state)
        )
      `)?.eq('id', courseId)?.single();
      
      if (error) throw error;
      return data;
    }, 'getCourseById');
  },

  async getCoursesByInstitute(instituteId) {
    return errorHandler?.withRetry(async () => {
      if (!validationUtils?.isValidUUID(instituteId)) {
        throw new Error('Invalid institute ID');
      }
      
      // Check cache
      const cacheKey = `courses_institute_${instituteId}`;
      const cached = dataCache?.get(cacheKey);
      if (cached) return cached;

      const { data, error } = await performanceUtils?.timeout(10000,
        supabase
          ?.from('courses')
          ?.select('*')
          ?.eq('institute_id', instituteId)
          ?.order('course_name', { ascending: true })
      );
      
      if (error) {
        console.error('Supabase query error:', error);
        throw error;
      }
      
      const result = data || [];
      
      // Cache result
      dataCache?.set(cacheKey, result);
      
      return result;
    }, { maxRetries: 2, retryDelay: 1000 });
  },

  async createCourse(courseData) {
    return errorHandler?.handleAsync(async () => {
      if (!validationUtils?.isValidUUID(courseData?.instituteId)) {
        throw new Error('Invalid institute ID');
      }
      
      // Sanitize inputs
      const sanitized = {
        institute_id: courseData?.instituteId,
        course_name: validationUtils?.sanitizeString(courseData?.courseName),
        category: validationUtils?.sanitizeString(courseData?.category),
        course_type: validationUtils?.sanitizeString(courseData?.courseType),
        specialization: courseData?.specialization ? validationUtils?.sanitizeString(courseData?.specialization) : null,
        level: validationUtils?.sanitizeString(courseData?.level),
        duration: validationUtils?.sanitizeString(courseData?.duration),
        fees: courseData?.fees,
        show_fee: courseData?.showFee !== false,
        eligibility: courseData?.eligibility ? validationUtils?.sanitizeString(courseData?.eligibility) : null,
        admission_process: courseData?.admissionProcess ? validationUtils?.sanitizeString(courseData?.admissionProcess) : null,
        placement_stats: courseData?.placementStats ? validationUtils?.sanitizeString(courseData?.placementStats) : null,
        accreditation: courseData?.accreditation ? validationUtils?.sanitizeString(courseData?.accreditation) : null
      };
      
      const { data, error } = await supabase?.from('courses')?.insert(sanitized)?.select()?.single();
      
      if (error) throw error;
      
      // Clear cache
      dataCache?.delete(`courses_institute_${courseData?.instituteId}`);
      
      return data;
    }, 'createCourse');
  },

  async updateCourse(courseId, courseData) {
    return errorHandler?.handleAsync(async () => {
      if (!validationUtils?.isValidUUID(courseId)) {
        throw new Error('Invalid course ID');
      }
      
      // Sanitize inputs
      const sanitized = {
        course_name: validationUtils?.sanitizeString(courseData?.courseName),
        category: validationUtils?.sanitizeString(courseData?.category),
        course_type: validationUtils?.sanitizeString(courseData?.courseType),
        specialization: courseData?.specialization ? validationUtils?.sanitizeString(courseData?.specialization) : null,
        level: validationUtils?.sanitizeString(courseData?.level),
        duration: validationUtils?.sanitizeString(courseData?.duration),
        fees: courseData?.fees,
        show_fee: courseData?.showFee !== false,
        eligibility: courseData?.eligibility ? validationUtils?.sanitizeString(courseData?.eligibility) : null,
        admission_process: courseData?.admissionProcess ? validationUtils?.sanitizeString(courseData?.admissionProcess) : null,
        placement_stats: courseData?.placementStats ? validationUtils?.sanitizeString(courseData?.placementStats) : null,
        accreditation: courseData?.accreditation ? validationUtils?.sanitizeString(courseData?.accreditation) : null
      };
      
      const { data, error } = await supabase?.from('courses')?.update(sanitized)?.eq('id', courseId)?.select()?.single();
      
      if (error) throw error;
      
      // Clear cache
      dataCache?.clear();
      
      return data;
    }, 'updateCourse');
  },

  async deleteCourse(courseId) {
    return errorHandler?.handleAsync(async () => {
      if (!validationUtils?.isValidUUID(courseId)) {
        throw new Error('Invalid course ID');
      }
      
      const { error } = await supabase?.from('courses')?.delete()?.eq('id', courseId);
      
      if (error) throw error;
      
      // Clear cache
      dataCache?.clear();
    }, 'deleteCourse');
  }
};
